import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import type React from "react"
import { Suspense, useEffect, useMemo } from "react"
import { Plus } from "react-feather"
import { useLocation, useNavigate } from "react-router-dom"
import {
  type TicketList200TicketsItem,
  type TicketListParams,
  ticketApi,
} from "@/api/client/ticket"
import { TicketCard } from "@/components/tickets/TicketCard"
import {
  type TicketFilterValues,
  TicketSearchFilter,
} from "@/components/tickets/TicketSearchFilter"
import { TicketStatusTabs, type TicketTabStatus } from "@/components/tickets/TicketStatusTabs"
import type { TicketStatus } from "@/components/tickets/type"
import { Button } from "@/components/ui/button/button"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Loading } from "@/components/ui/loading"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination/pagination"

const ITEMS_PER_PAGE = 10

// ページ番号を生成するヘルパー関数
function renderPageNumbers(
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void
) {
  const pages: React.ReactNode[] = []
  const maxVisiblePages = 5

  // 表示するページ番号の範囲を計算
  let startPage: number
  let endPage: number

  if (totalPages <= maxVisiblePages) {
    startPage = 1
    endPage = totalPages
  } else if (currentPage <= 3) {
    startPage = 1
    endPage = maxVisiblePages
  } else if (currentPage >= totalPages - 2) {
    startPage = totalPages - maxVisiblePages + 1
    endPage = totalPages
  } else {
    startPage = currentPage - 2
    endPage = currentPage + 2
  }

  // 先頭の省略記号
  if (startPage > 1) {
    pages.push(
      <PaginationItem key="first">
        <PaginationLink
          href="#"
          onClick={(e) => {
            e.preventDefault()
            onPageChange(1)
          }}
          className="cursor-pointer font-semibold text-primary"
        >
          1
        </PaginationLink>
      </PaginationItem>
    )
    if (startPage > 2) {
      pages.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      )
    }
  }

  // ページ番号
  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <PaginationItem key={i}>
        <PaginationLink
          href="#"
          onClick={(e) => {
            e.preventDefault()
            onPageChange(i)
          }}
          isActive={i === currentPage}
          className="cursor-pointer font-semibold text-primary"
        >
          {i}
        </PaginationLink>
      </PaginationItem>
    )
  }

  // 末尾の省略記号
  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pages.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      )
    }
    pages.push(
      <PaginationItem key="last">
        <PaginationLink
          href="#"
          onClick={(e) => {
            e.preventDefault()
            onPageChange(totalPages)
          }}
          className="cursor-pointer font-semibold text-primary"
        >
          {totalPages}
        </PaginationLink>
      </PaginationItem>
    )
  }

  return pages
}

// nuqsのパーサー定義
const ticketsPageParsers = {
  page: parseAsInteger.withDefault(1),
  status: parseAsString,
  titleLike: parseAsString,
  propertyId: parseAsInteger,
  roomId: parseAsInteger,
  tenantId: parseAsInteger,
  category: parseAsString,
  contractorType: parseAsString,
  contractorStatus: parseAsString,
  showOnlyUnread: parseAsBoolean,
  excludeCompleted: parseAsBoolean,
}

function formatTicketDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    return date
      .toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(/\//g, "-")
  } catch {
    return dateString
  }
}

interface TicketsPageContentProps {
  queryParams: {
    page: number
    status: string | null
    titleLike: string | null
    propertyId: number | null
    roomId: number | null
    tenantId: number | null
    category: string | null
    contractorType: string | null
    contractorStatus: string | null
    showOnlyUnread: boolean | null
    excludeCompleted: boolean | null
  }
  onPageChange: (page: number) => void
  shouldRefetch?: boolean
}

function TicketsPageContent({ queryParams, onPageChange, shouldRefetch }: TicketsPageContentProps) {
  const navigate = useNavigate()
  const location = useLocation()

  // APIリクエストパラメータを構築
  const apiParams = useMemo((): TicketListParams => {
    const params: TicketListParams = {
      page: queryParams.page,
      limit: ITEMS_PER_PAGE,
      withProperty: "true",
      withRoom: "true",
    }

    // ステータスフィルタ
    if (queryParams.status && queryParams.status !== "all") {
      params.status = queryParams.status as TicketListParams["status"]
    }

    // 完了を除外
    if (queryParams.excludeCompleted) {
      params.excludeStatus = "completed"
    }

    // タイトル検索フィルタ
    if (queryParams.titleLike) {
      params.titleLike = queryParams.titleLike
    }

    // 物件フィルタ
    if (queryParams.propertyId) {
      params.propertyId = queryParams.propertyId
    }

    if (queryParams.roomId) {
      params.roomId = queryParams.roomId
    }

    if (queryParams.tenantId) {
      params.tenantId = queryParams.tenantId
    }

    // カテゴリフィルタ
    if (queryParams.category) {
      params.category = queryParams.category as TicketListParams["category"]
    }

    // 業者タイプフィルタ
    if (queryParams.contractorType) {
      params.contractorType = queryParams.contractorType as TicketListParams["contractorType"]
    }

    // 業者ステータスフィルタ
    if (queryParams.contractorStatus) {
      params.contractorStatus = queryParams.contractorStatus as TicketListParams["contractorStatus"]
    }

    // 未読のみ
    if (queryParams.showOnlyUnread) {
      params.showOnlyUnread = "true"
    }

    return params
  }, [queryParams])

  // API呼び出し
  const { data, error, refetch } = ticketApi.useTicketListSuspense(apiParams)

  // navigation stateからrefetchフラグをチェック
  useEffect(() => {
    if (shouldRefetch) {
      refetch()
      // stateをクリアして再度refetchが発生しないようにする
      window.history.replaceState({}, "")
    }
  }, [shouldRefetch, refetch])

  // エラーがある場合はthrowして、ErrorBoundaryにキャッチさせる
  if (error) {
    throw error
  }

  const tickets = data?.tickets || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  // ページ番号が範囲外の場合は1ページ目にリダイレクト
  useEffect(() => {
    if (totalPages > 0 && (queryParams.page < 1 || queryParams.page > totalPages)) {
      onPageChange(1)
    }
  }, [queryParams.page, totalPages, onPageChange])

  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch()
    }
  }, [location.state, refetch])

  const handlePageChange = (page: number) => {
    onPageChange(page)
    // ページトップにスクロール
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleTicketClick = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`)
  }

  return (
    <>
      <div className="space-y-2.5">
        <p className="text-base font-semibold text-[#354267]">
          {totalCount}件{queryParams.titleLike && ` ("${queryParams.titleLike}"の検索結果)`}
        </p>
        {tickets.map((ticket: TicketList200TicketsItem) => (
          <TicketCard
            key={ticket.id}
            title={ticket.title}
            propertyName={ticket.property?.name || "-"}
            roomName={ticket.room?.name || "-"}
            status={ticket.status as TicketStatus}
            createdAt={formatTicketDate(ticket.createdAt)}
            onClick={() => handleTicketClick(ticket.id)}
          />
        ))}
        {tickets.length === 0 && (
          <div className="bg-slate-100 rounded-md p-8 text-center">
            <p className="text-sm font-semibold text-slate-400">該当する案件がありません</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pt-5">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (queryParams.page > 1) handlePageChange(queryParams.page - 1)
                  }}
                  className={
                    queryParams.page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {/* ページ番号 */}
              {renderPageNumbers(queryParams.page, totalPages, handlePageChange)}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (queryParams.page < totalPages) handlePageChange(queryParams.page + 1)
                  }}
                  className={
                    queryParams.page === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}

export function TicketsPage() {
  const navigate = useNavigate()
  const location = useLocation()

  // navigation stateからrefetchフラグを取得
  const shouldRefetch = location.state?.shouldRefetch as boolean | undefined

  // URLクエリパラメータ管理
  const [queryParams, setQueryParams] = useQueryStates(ticketsPageParsers)

  // URLパラメータからフィルター値を復元
  const filters: TicketFilterValues = useMemo(
    () => ({
      keyword: queryParams.titleLike || undefined,
      property: queryParams.propertyId ? String(queryParams.propertyId) : undefined,
      category: queryParams.category || undefined,
      assignee: queryParams.contractorType || undefined,
      status: queryParams.contractorStatus || undefined,
      showOnlyUnread: queryParams.showOnlyUnread || false,
      showCompleted: queryParams.excludeCompleted ? false : undefined,
    }),
    [queryParams]
  )

  // 有効なフィルターパラメータがあるかどうか
  const hasActiveFilters = useMemo(() => {
    return !!(
      queryParams.propertyId ||
      queryParams.category ||
      queryParams.contractorType ||
      queryParams.contractorStatus ||
      queryParams.showOnlyUnread ||
      queryParams.excludeCompleted
    )
  }, [queryParams])

  // 現在のステータスタブ
  const activeTab: TicketTabStatus = (queryParams.status as TicketTabStatus) || "all"

  // タブ変更時にページをリセット
  const handleTabChange = (tab: TicketTabStatus) => {
    setQueryParams({
      status: tab === "all" ? null : tab,
      page: 1,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  const handleSearchChange = (value: string) => {
    // debounceは不要、検索ボタン押下時に反映
    // 入力値はローカルで保持せずURLパラメータとして管理
    setQueryParams({
      titleLike: value || null,
      page: 1,
    })
  }

  const handleSearch = () => {
    // 検索ボタン押下時の処理
    // URLパラメータは既に更新済みなので追加処理は不要
  }

  const handleApplyFilters = (newFilters: TicketFilterValues) => {
    setQueryParams({
      titleLike: newFilters.keyword || null,
      propertyId: newFilters.property ? Number.parseInt(newFilters.property, 10) : null,
      category: newFilters.category || null,
      contractorType: newFilters.assignee || null,
      contractorStatus: newFilters.status || null,
      showOnlyUnread: newFilters.showOnlyUnread || null,
      excludeCompleted: newFilters.showCompleted === false ? true : null,
      page: 1,
    })
  }

  const handleCreateTicket = () => {
    navigate("/tickets/add")
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[#354267]">案件一覧</h1>
        <Button onClick={handleCreateTicket}>
          <Plus className="w-4 h-4 mr-2" />
          新規案件作成
        </Button>
      </div>

      <TicketStatusTabs activeTab={activeTab} onTabChange={handleTabChange} />

      <TicketSearchFilter
        searchValue={queryParams.titleLike || ""}
        onSearchChange={handleSearchChange}
        onSearch={handleSearch}
        filters={filters}
        onFiltersApply={handleApplyFilters}
        defaultOpen={hasActiveFilters}
      />

      <ReactErrorBoundary>
        <Suspense
          fallback={<Loading className="mt-10" size="lg" text="案件情報を読み込んでいます..." />}
        >
          <TicketsPageContent
            queryParams={queryParams}
            onPageChange={handlePageChange}
            shouldRefetch={shouldRefetch}
          />
        </Suspense>
      </ReactErrorBoundary>
    </div>
  )
}
